"use client";

import { useEffect, useSyncExternalStore } from "react";
import type { ServiceStatus } from "@/lib/serviceStatus";

const HEALTHY_POLL_MS = 120_000;
const OUTAGE_POLL_MS = 30_000;

// One poller per tab, shared by every component that calls the hook (the
// design page, ResultViewer's EditPanel, the Pro editor), so mounting several
// consumers never multiplies the health checks.
interface Snapshot {
  status: ServiceStatus | null;
  checking: boolean;
}

let snapshot: Snapshot = { status: null, checking: false };
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;
let subscribers = 0;

function emit(next: Partial<Snapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l());
}

async function recheck() {
  if (timer) clearTimeout(timer);
  timer = null;
  emit({ checking: true });
  let next: ServiceStatus | null = null;
  try {
    const res = await fetch("/api/service-status", { cache: "no-store" });
    if (res.ok) next = (await res.json()) as ServiceStatus;
  } catch {
    // fail open — keep whatever we knew before
  }
  emit(next ? { status: next, checking: false } : { checking: false });
  const down = snapshot.status ? !snapshot.status.ok : false;
  if (subscribers > 0) {
    timer = setTimeout(recheck, down ? OUTAGE_POLL_MS : HEALTHY_POLL_MS);
  }
}

function onVisible() {
  if (document.visibilityState === "visible") recheck();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => snapshot;
// Must return the SAME object every call: useSyncExternalStore compares
// snapshots by identity, so a fresh literal reads as a change on each render.
const SERVER_SNAPSHOT: Snapshot = { status: null, checking: false };
const getServerSnapshot = () => SERVER_SNAPSHOT;

/**
 * Checks Designer's backend dependencies at startup and keeps polling
 * (PLA2-945). `unavailable` is true only when the status route answered and
 * reported a blocking service down — if the route itself fails we fail open,
 * because blocking the whole designer on a flaky health probe is worse than
 * the per-request errors we already show.
 */
export function useServiceStatus() {
  const { status, checking } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  useEffect(() => {
    subscribers += 1;
    if (subscribers === 1) {
      document.addEventListener("visibilitychange", onVisible);
      recheck();
    }
    return () => {
      subscribers -= 1;
      if (subscribers === 0) {
        document.removeEventListener("visibilitychange", onVisible);
        if (timer) clearTimeout(timer);
        timer = null;
      }
    };
  }, []);

  return {
    status,
    unavailable: status ? !status.ok : false,
    checking,
    recheck,
  };
}
